/**
 * Zentraler fetch-Wrapper.
 *
 * - Auto-Auth-Header (wenn Token vorhanden)
 * - Auto-Logout bei 401
 * - JSON-Parsing
 * - Pydantic-422-Errors lesbar machen
 *
 * Alle UI-fetches sollten hierdurch laufen — nicht direkt fetch().
 */

class ApiError extends Error {
  constructor(message, { status = 0, details = null } = {}) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

export function makeApi({ getToken, onUnauthorized }) {
  const baseHeaders = () => {
    const h = { 'Content-Type': 'application/json' };
    const t = getToken?.();
    if (t) h.Authorization = `Bearer ${t}`;
    return h;
  };

  async function request(method, url, body) {
    const opts = { method, headers: baseHeaders() };
    if (body !== undefined) opts.body = JSON.stringify(body);
    const res = await fetch(url, opts);

    if (res.status === 401) {
      onUnauthorized?.();
      throw new ApiError('Unauthorized', { status: 401 });
    }

    // 422 → Pydantic Validation Errors
    if (res.status === 422) {
      const j = await res.json().catch(() => null);
      throw new ApiError(j?.error || 'Validation failed', {
        status: 422,
        details: j?.details || null,
      });
    }

    if (!res.ok) {
      const j = await res.json().catch(() => null);
      throw new ApiError(j?.error || res.statusText, { status: res.status });
    }

    // 204 oder leerer Body
    const txt = await res.text();
    return txt ? JSON.parse(txt) : null;
  }

  return {
    get:    (url)       => request('GET', url),
    post:   (url, body) => request('POST', url, body),
    patch:  (url, body) => request('PATCH', url, body),
    delete: (url, body) => request('DELETE', url, body),
    raw:    (url, opts) => fetch(url, opts),  // für SSE-Streams etc.
  };
}

export { ApiError };
